import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import BrandLogo from "@/components/BrandLogo";
import { CalendarDays, Ban, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManageBooking() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [newStart, setNewStart] = useState("");
  const [mode, setMode] = useState("view"); // view | reschedule
  const [msg, setMsg] = useState("");

  useEffect(() => {
    axios.get(`${API}/lp/booking/${token}`)
      .then((r) => setBooking(r.data))
      .catch(() => setError("This booking link is not valid or has expired."));
  }, [token]);

  const fmt = (iso) =>
    new Date(iso).toLocaleString("en-GB", {
      timeZone: booking?.timezone || "Europe/London",
      weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
    });

  const cancel = async () => {
    if (!window.confirm("Cancel this appointment?")) return;
    setBusy(true);
    setMsg("");
    try {
      const r = await axios.post(`${API}/lp/booking/${token}/cancel`);
      setBooking(r.data);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Could not cancel — please contact the business.");
    } finally {
      setBusy(false);
    }
  };

  const reschedule = async () => {
    if (!newStart) return;
    setBusy(true);
    setMsg("");
    try {
      const r = await axios.post(`${API}/lp/booking/${token}/reschedule`, { start: newStart });
      setBooking(r.data);
      setMode("view");
      setNewStart("");
    } catch (e) {
      setMsg(e.response?.data?.detail || "That time is not available — please pick another.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white" data-testid="manage-booking-page">
      <header className="border-b border-neutral-200">
        <div className="mx-auto flex max-w-xl items-center px-5 py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="manage-brand">
            <BrandLogo />
            <span className="text-base font-bold tracking-tight">Savy Kerno</span>
          </Link>
        </div>
      </header>
      <main className="mx-auto w-full max-w-xl flex-1 px-5 py-12">
        {error && (
          <div className="rounded-2xl border border-neutral-200 p-8 text-center" data-testid="manage-error">
            <p className="text-sm font-semibold text-[#111111]">{error}</p>
            <p className="mt-2 text-xs text-neutral-500">Please contact the business that sent you this link.</p>
          </div>
        )}
        {!error && !booking && <p className="text-center text-sm text-neutral-400">Loading your booking…</p>}
        {booking && (
          <div className="rounded-2xl border border-neutral-200 p-8" data-testid="manage-card">
            <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">{booking.business_name}</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight" data-testid="manage-title">
              {booking.status === "Cancelled" ? "Appointment cancelled" : "Your appointment"}
            </h1>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between border-b border-neutral-100 pb-3">
                <span className="text-neutral-500">Service</span>
                <span className="font-semibold" data-testid="manage-service">{booking.service_name}</span>
              </div>
              <div className="flex justify-between border-b border-neutral-100 pb-3">
                <span className="text-neutral-500">With</span>
                <span className="font-semibold" data-testid="manage-staff">{booking.staff_name}</span>
              </div>
              <div className="flex justify-between border-b border-neutral-100 pb-3">
                <span className="text-neutral-500">When</span>
                <span className="text-right font-semibold" data-testid="manage-when">{fmt(booking.start)}</span>
              </div>
              <div className="flex justify-between pb-1">
                <span className="text-neutral-500">Status</span>
                <span className="font-semibold" data-testid="manage-status">{booking.status}</span>
              </div>
            </div>

            {msg && <p className="mt-4 rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600" data-testid="manage-message">{msg}</p>}

            {booking.status === "Confirmed" && mode === "view" && (
              <div className="mt-6 space-y-2.5">
                <button
                  onClick={() => setMode("reschedule")}
                  data-testid="manage-reschedule-open"
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-neutral-50"
                >
                  <CalendarClock size={15} /> Reschedule
                </button>
                <button
                  onClick={cancel}
                  disabled={busy}
                  data-testid="manage-cancel-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
                >
                  <Ban size={15} /> {busy ? "Cancelling…" : "Cancel appointment"}
                </button>
                <p className="pt-1 text-center text-[11px] text-neutral-400">
                  This business asks for at least {booking.cancel_notice_hours} hours notice to cancel and {booking.min_booking_notice_hours} hours notice to move a booking.
                </p>
              </div>
            )}

            {booking.status === "Confirmed" && mode === "reschedule" && (
              <div className="mt-6 space-y-3" data-testid="manage-reschedule-panel">
                <label className="block text-xs font-bold uppercase tracking-widest text-neutral-400">New date and time</label>
                <input
                  type="datetime-local"
                  value={newStart}
                  onChange={(e) => setNewStart(e.target.value)}
                  data-testid="manage-reschedule-input"
                  className="w-full rounded-xl border border-neutral-300 px-4 py-2.5 text-sm outline-none focus:border-[#111111]"
                />
                <div className="flex gap-2.5">
                  <button
                    onClick={() => { setMode("view"); setMsg(""); }}
                    data-testid="manage-reschedule-back"
                    className="flex-1 rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-neutral-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={reschedule}
                    disabled={busy || !newStart}
                    data-testid="manage-reschedule-confirm"
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#111111] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-neutral-800 disabled:opacity-50"
                  >
                    <CalendarDays size={15} /> {busy ? "Saving…" : "Confirm new time"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
      <footer className="border-t border-neutral-100 py-5 text-center text-[11px] text-neutral-400">
        Powered by Savy Kerno
      </footer>
    </div>
  );
}
